import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Music, Star, Loader2, CheckCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const LeaveReview = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { user, loading: authLoading } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [orderValid, setOrderValid] = useState(false);
  const [occasion, setOccasion] = useState("");

  useEffect(() => {
    const checkOrder = async () => {
      if (!orderId || !user) {
        setLoading(false);
        return;
      }

      // Check if order exists, belongs to user, and is completed
      const { data: order } = await supabase
        .from("song_orders")
        .select("id, status, event_type, recipient_names")
        .eq("id", orderId)
        .eq("user_id", user.id)
        .eq("status", "completed")
        .maybeSingle();

      if (order) {
        setOrderValid(true);
        setOccasion(order.event_type);
      }

      // Check if already reviewed
      const { data: existing } = await supabase
        .from("reviews")
        .select("id")
        .eq("order_id", orderId)
        .maybeSingle();

      if (existing) {
        setAlreadyReviewed(true);
      }

      // Pre-fill name from profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("user_id", user.id)
        .maybeSingle();

      if (profile?.full_name) {
        setReviewerName(profile.full_name);
      }

      setLoading(false);
    };

    if (!authLoading) checkOrder();
  }, [orderId, user, authLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !orderId || rating === 0) return;

    setSubmitting(true);
    const { error } = await supabase.from("reviews").insert({
      order_id: orderId,
      user_id: user.id,
      rating,
      review_text: reviewText.trim() || null,
      reviewer_name: reviewerName.trim() || null,
      occasion,
    });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setSubmitting(false);
      return;
    }

    // Notify admin about new review
    supabase.functions.invoke("send-order-notification", {
      body: { order_id: orderId, type: "new_review" },
    }).catch(console.error);

    setSubmitted(true);
    setSubmitting(false);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background music-note-pattern">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-2xl shadow-elevated p-8"
          >
            {submitted ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-foreground">
                  Thank You! 🎶
                </h1>
                <p className="text-muted-foreground">
                  Your review means the world to us. Thank you for sharing your experience!
                </p>
                <Link to="/dashboard">
                  <Button variant="outline" className="mt-4">Back to Dashboard</Button>
                </Link>
              </div>
            ) : !user ? (
              <div className="text-center space-y-4">
                <h1 className="font-heading text-2xl font-bold text-foreground">Sign In Required</h1>
                <p className="text-muted-foreground">Please sign in to leave a review.</p>
                <Link to="/login">
                  <Button className="mt-4">Sign In</Button>
                </Link>
              </div>
            ) : alreadyReviewed ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Star className="w-8 h-8 text-primary" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-foreground">Already Reviewed</h1>
                <p className="text-muted-foreground">You've already left a review for this order. Thank you!</p>
                <Link to="/dashboard">
                  <Button variant="outline" className="mt-4">Back to Dashboard</Button>
                </Link>
              </div>
            ) : !orderValid ? (
              <div className="text-center space-y-4">
                <h1 className="font-heading text-2xl font-bold text-foreground">Review Not Available</h1>
                <p className="text-muted-foreground">This order is not eligible for a review. Reviews are available for completed orders.</p>
                <Link to="/dashboard">
                  <Button variant="outline" className="mt-4">Back to Dashboard</Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Music className="w-7 h-7 text-primary" />
                  </div>
                  <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
                    How Was Your Experience?
                  </h1>
                  <p className="text-muted-foreground">
                    We'd love to hear about your experience with Your Moment Song!
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Star Rating */}
                  <div className="space-y-2">
                    <Label>Rating</Label>
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          className="transition-transform hover:scale-110"
                        >
                          <Star
                            className={`w-10 h-10 ${
                              star <= (hoverRating || rating)
                                ? "fill-primary text-primary"
                                : "text-muted-foreground/30"
                            } transition-colors`}
                          />
                        </button>
                      ))}
                    </div>
                    {rating === 0 && (
                      <p className="text-center text-sm text-muted-foreground">Click a star to rate</p>
                    )}
                  </div>

                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="reviewerName">Your Name (displayed with review)</Label>
                    <Input
                      id="reviewerName"
                      value={reviewerName}
                      onChange={(e) => setReviewerName(e.target.value)}
                      placeholder="How should we display your name?"
                      maxLength={100}
                    />
                  </div>

                  {/* Review Text */}
                  <div className="space-y-2">
                    <Label htmlFor="reviewText">Your Review <span className="text-destructive">*</span></Label>
                    <Textarea
                      id="reviewText"
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Tell us about your experience..."
                      rows={4}
                      maxLength={1000}
                      required
                    />
                    {reviewText.trim().length === 0 && (
                      <p className="text-sm text-muted-foreground">A comment is required to submit your review.</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    variant="hero"
                    size="lg"
                    className="w-full"
                    disabled={submitting || rating === 0 || reviewText.trim().length === 0}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit Review
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LeaveReview;
